import 'bootstrap/dist/css/bootstrap.min.css';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PrivateRoute from '../src/Components/PrivateRoute';
import Layout from '../src/Layout/Layout';
import admin from '../src/_routes/admin';
import LanguageWrapper from './Components/LanguageWrapper';
import SuccessfulSignUp from './Components/SuccessfulSignUp';
import Attendance from './Pages/admin/Events/EventDetails/Attendance';
import Courses from './Pages/admin/Events/EventDetails/Courses';
import EventDetailsLayout from './Pages/admin/Events/EventDetails/EventDetailsLayout';
import EventOverview from './Pages/admin/Events/EventDetails/EventOverview';
import EventParticipants from './Pages/admin/Events/EventDetails/EventParticipants';
import EventResoucePerson from './Pages/admin/Events/EventDetails/EventResoucePerson';
import EventSessions from './Pages/admin/Events/EventDetails/EventSessions';
import PostEventActivities from './Pages/admin/Events/EventDetails/PostEventActivities';
import Forgot from './Pages/auth/Forgot';
import Login from './Pages/auth/Login';
import Reset from './Pages/auth/Reset/Reset';
import RegistrationWrapper from './Pages/auth/Roster/RegistrationWrapper';
import RosterSignUp from './Pages/auth/Roster/RosterSignUp';
import IndividualStep0 from './Pages/auth/Roster/Steps/Individual/IndividualStep0';
import IndividualStep1 from './Pages/auth/Roster/Steps/Individual/IndividualStep1';
import IndividualStep2 from './Pages/auth/Roster/Steps/Individual/IndividualStep2';
import IndividualStep3 from './Pages/auth/Roster/Steps/Individual/IndividualStep3';
import IndividualStep4 from './Pages/auth/Roster/Steps/Individual/IndividualStep4';
import OrganizationStep0 from './Pages/auth/Roster/Steps/Organization/OrganizationStep0';
import OrganizationStep1 from './Pages/auth/Roster/Steps/Organization/OrganizationStep1';
import OrganizationStep2 from './Pages/auth/Roster/Steps/Organization/OrganizationStep2';
import OrganizationStep3 from './Pages/auth/Roster/Steps/Organization/OrganizationStep3';
import OrganizationStep4 from './Pages/auth/Roster/Steps/Organization/OrganizationStep4';
import OrganizationStep5 from './Pages/auth/Roster/Steps/Organization/OrganizationStep5';
import TraineeStep0 from './Pages/auth/Trainee/Steps/TraineeStep0';
import TraineeStep1 from './Pages/auth/Trainee/Steps/TraineeStep1';
import TraineeStep2 from './Pages/auth/Trainee/Steps/TraineeStep2';
import { TraineeSignUp } from './Pages/auth/Trainee/TraineeSignUp';
import NotFound from './Pages/error/NotFound';
import About from './Pages/user/Home/About';
import Home from './Pages/user/Home/Home';
import EventViewWrapper from './Pages/user/Home/sections/EventViewWrapper';
import Support from './Pages/user/Home/Support';
import { fetchActiveFiscalYear } from './Redux/slices/fiscalyear';
import { clearUiMessage } from './Redux/slices/ui';
import { getUserData } from './Redux/slices/user';
import './scss/style.scss';
import Checker from './_helpers/hooks/Checker';
import useNotificationAlert from './_helpers/hooks/useNotificationAlert';

function App() {
  const message = useSelector((state) => state.ui.message);
  const local = useSelector((state) => state.local.local);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const user = useSelector((state) => state.user.user);
  const appliedEvent = useSelector((state) => state.event.appliedEvent);
  useNotificationAlert();

  const dispatch = useDispatch();
  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getUserData());
      dispatch(fetchActiveFiscalYear());
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (message) {
      if (message.error === false) {
        toast.success(message.message);
        clearUiMessage();
      } else if (message) {
        toast.error(message.message);
        clearUiMessage();
      } else {
        toast.success(message);
      }
    }
  }, [message, clearUiMessage]);

  return (
    <div className="App">
      <Checker user={user} isLoggedIn={isLoggedIn} />
      <LanguageWrapper local={local}>
        <Routes>
          <Route path="/" element={<Home />}></Route>
          <Route path="/about" element={<About />}></Route>
          <Route path="/support" element={<Support />}></Route>
          <Route
            path="/signin"
            element={
              isLoggedIn ? (
                <Navigate to={appliedEvent ? `/events/${appliedEvent}` : `/admin`} />
              ) : (
                <Login />
              )
            }></Route>
          <Route path="/forgot" element={<Forgot />}></Route>
          <Route path="/password/reset" element={<Reset />}></Route>
          <Route path="/trainees/apply" element={<TraineeSignUp />}>
            <Route index element={<TraineeStep0 />}></Route>
            <Route path="1" element={<TraineeStep1 />}></Route>
            <Route path="2" element={<TraineeStep2 />}></Route>
          </Route>

          <Route path="/instructions/:type/:email" element={<SuccessfulSignUp />}></Route>

          <Route path="/rosters/apply" element={<RosterSignUp />}></Route>

          <Route path="/rosters/individual_apply" element={<RegistrationWrapper />}>
            <Route index element={<IndividualStep0 />}></Route>
            <Route path="1" element={<IndividualStep1 />}></Route>
            <Route path="2" element={<IndividualStep2 />}></Route>
            <Route path="3" element={<IndividualStep3 />}></Route>
            <Route path="4" element={<IndividualStep4 />}></Route>
          </Route>

          <Route path="/rosters/organization_apply" element={<RegistrationWrapper />}>
            <Route index element={<OrganizationStep0 />}></Route>
            <Route path="1" element={<OrganizationStep1 />}></Route>
            <Route path="2" element={<OrganizationStep2 />}></Route>
            <Route path="3" element={<OrganizationStep3 />}></Route>
            <Route path="4" element={<OrganizationStep4 />}></Route>
            <Route path="5" element={<OrganizationStep5 />}></Route>
          </Route>

          <Route path="/events" element={<EventViewWrapper />}>
            <Route path=":id" element={<EventDetailsLayout />}>
              <Route index element={<EventOverview />}></Route>
              <Route path="view/:token" element={<EventOverview />}></Route>
              <Route path="participants" element={<EventParticipants />}></Route>
              <Route path="roster" element={<EventResoucePerson />}></Route>
              <Route path="event_sessions" element={<EventSessions />}></Route>
              <Route path="attendence" element={<Attendance />}></Route>
              <Route path="documents" element={<Attendance />}></Route>
              <Route path="courses" element={<Courses />}></Route>
              <Route path="post_event_activity" element={<PostEventActivities />}></Route>
            </Route>
          </Route>
          {/* {isLoggedIn ? admin : <Route path="/signin" element={<Navigate to="/signin" />} />} */}
          <Route
            path="/admin"
            element={
              <PrivateRoute isLoggedIn={isLoggedIn}>
                <Layout />
              </PrivateRoute>
            }>
            {admin}
          </Route>
          <Route path="/not-found" element={<NotFound />} />
          <Route path="*" element={<Navigate to="not-found" />} />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </LanguageWrapper>
    </div>
  );
}

export default App;
